/**
 * Fetches AI context insights based on active panel and inputs.
 * Mock backend - later will connect to webserver + ledger hook.
 * 
 * @param panel - Active panel identifier (e.g., "Construction", "ETS Bridge")
 * @param inputs - Input data object for context
 * @returns Array of insight strings
 */
export async function fetchAIContextInsights(panel: string, inputs: any): Promise<string[]> {
  // MOCK backend – később webszerver + ledger hook
  await new Promise((r) => setTimeout(r, 300));

  if (panel === "Construction") {
    return [
      `High CO₂ concentration from concrete: consider fly-ash substitution.`,
      `Steel intensity above sector average (+14%).`,
      `Mineral insulation is taxonomy aligned.`,
      `Potential reduction: -820kg CO₂e with greener mix.`,
    ];
  }

  if (panel === "ETS Bridge") {
    return [
      `ETS channel not official – ensure registry verification.`,
      `Evidence export must match MRV (Monitoring Reporting Verification).`,
    ];
  }

  if (panel === "ScopeExchangePanel" || panel === "Scope Exchange") {
    const integrityScore = (inputs.integrityScore as number) || 100;
    const reconciliationDelayDays = (inputs.reconciliationDelayDays as number) || 0;
    const riskProp = (inputs.riskPropagation as number) || 0;

    return [
      `Downstream propagation increases Article 8 reconciliation friction by ${riskProp.toFixed(1)}%.`,
      integrityScore < 80
        ? `Integrity score ${integrityScore}% below optimal. Verify counterpart data sources.`
        : `Integrity score ${integrityScore}% within acceptable range.`,
      reconciliationDelayDays > 7
        ? `Reconciliation delay ${reconciliationDelayDays} days exceeds 7-day threshold. Risk of compliance gap.`
        : `Reconciliation delay ${reconciliationDelayDays} days acceptable.`,
      `Consider optimizing payload sizes to reduce propagation coefficient.`,
    ];
  }

  if (panel === "eSealPanel" || panel === "eSeal / PEPPOL") {
    const sealConfidence = (inputs.sealConfidence as number) || 0;
    const validatorRegion = (inputs.validatorRegion as string) || "EU";

    return [
      `PEPPOL anchor improves cross-border invoice admissibility by ${sealConfidence}% confidence.`,
      `Validator region: ${validatorRegion}. Ensure matches transaction jurisdiction.`,
      sealConfidence >= 80
        ? `High seal confidence (${sealConfidence}%) ensures strong legal validity.`
        : sealConfidence >= 70
        ? `Moderate seal confidence (${sealConfidence}%). Consider additional verification.`
        : `Low seal confidence (${sealConfidence}%). Regenerate seal or improve document integrity.`,
      `Ledger anchoring provides tamper-proof evidence chain.`,
    ];
  }

  if (panel === "CertificateNFTPanel" || panel === "Certificate NFT") {
    const esgScore = (inputs.esgScore as number) || 0;
    const certificateLevel = (inputs.certificateLevel as string) || "bronze";
    const revocationRisk = (inputs.revocationRisk as number) || 0;

    return [
      `Certificate level influences downstream tender preference under EU GPP guidelines.`,
      `Current level: ${certificateLevel.toUpperCase()} (ESG Score: ${esgScore}%).`,
      certificateLevel === "platinum" || certificateLevel === "gold"
        ? `High certificate level improves competitive positioning in public procurement.`
        : `Consider improving ESG score to reach higher certificate level (${certificateLevel === "bronze" ? "silver" : "gold"} recommended).`,
      revocationRisk > 0
        ? `⚠️ Revocation risk detected (${(revocationRisk * 100).toFixed(1)}%). Maintain compliance to prevent revocation.`
        : `Certificate status: Active. Maintain ESG standards to preserve validity.`,
    ];
  }

  if (panel === "EUFabricPanel" || panel === "EU Scope Exchange Fabric") {
    const replicationLagMS = (inputs.replicationLagMS as number) || 0;
    const reconciliationErrors = (inputs.reconciliationErrors as number) || 0;
    const anomalyFlags = (inputs.anomalyFlags as number) || 0;
    const escalationLevel = (inputs.escalationLevel as string) || "info";
    const fabricStatus = (inputs.fabricStatus as number) || 100;

    return [
      `Cross-border packet friction degrades reconciliation throughput.`,
      `Current replication lag: ${replicationLagMS}ms. Target: <200ms for optimal performance.`,
      reconciliationErrors > 0
        ? `Reconciliation errors detected: ${reconciliationErrors}. Investigate data source integrity.`
        : `Reconciliation status: Healthy.`,
      anomalyFlags > 0
        ? `Anomaly flags: ${anomalyFlags}. Review cross-border data flows for inconsistencies.`
        : `Anomaly detection: Clear.`,
      escalationLevel === "critical"
        ? `⚠️ Critical escalation level. Immediate intervention required.`
        : escalationLevel === "warning"
        ? `⚠️ Warning escalation. Monitor fabric status closely.`
        : `Fabric status: ${fabricStatus.toFixed(1)}%. Operational within acceptable parameters.`,
    ];
  }

  if (panel === "EvidenceChainPanel" || panel === "EU Evidence Chain") {
    const chainIntegrityScore = (inputs.chainIntegrityScore as number) || 100;
    const admissibilityLevel = (inputs.admissibilityLevel as string) || "high";
    const chainVolatility = (inputs.chainVolatility as number) || 0;

    return [
      `High admissibility indicates cross-border judicial compatibility under eIDAS.`,
      `Current chain integrity: ${chainIntegrityScore.toFixed(1)}% (Admissibility: ${admissibilityLevel.toUpperCase()}).`,
      chainIntegrityScore >= 80
        ? `Excellent chain integrity ensures high admissibility across EU member states.`
        : chainIntegrityScore >= 50
        ? `Moderate chain integrity. Consider improving evidence item quality and reducing volatility.`
        : `Low chain integrity (${chainIntegrityScore.toFixed(1)}%) may impact cross-border admissibility. Review evidence items.`,
      chainVolatility > 10
        ? `⚠️ High chain volatility (${chainVolatility.toFixed(2)}). Consider stabilizing evidence chain.`
        : `Chain volatility within acceptable parameters.`,
      `Maintain chain integrity above 80% for optimal cross-border judicial compatibility.`,
    ];
  }

  if (panel === "TenderMeshPanel" || panel === "Global Tender Integrity Mesh") {
    const transparencyScore = (inputs.transparencyScore as number) || 100;
    const procurementRiskScore = (inputs.procurementRiskScore as number) || 0;
    const escalationLevel = (inputs.escalationLevel as string) || "low";
    const conflictPenalty = (inputs.conflictPenalty as number) || 0;
    const corruptionSignals = (inputs.corruptionSignals as number) || 0;

    return [
      `Global mesh degradation increases systemic procurement volatility.`,
      `Current transparency score: ${transparencyScore}%. Procurement risk: ${procurementRiskScore.toFixed(1)}%.`,
      escalationLevel === "high"
        ? `⚠️ High escalation level. Immediate intervention required. Review conflict of interest flags and corruption signals.`
        : escalationLevel === "medium"
        ? `⚠️ Medium escalation level. Monitor mesh integrity closely.`
        : `Mesh integrity: Operational. Procurement risk within acceptable parameters.`,
      conflictPenalty > 0
        ? `Conflict penalty: ${conflictPenalty.toFixed(1)}. Reduce conflict of interest flags to improve mesh integrity.`
        : `No conflict penalties detected.`,
      corruptionSignals > 0
        ? `Corruption signals detected: ${corruptionSignals}. Investigate and mitigate to reduce procurement risk.`
        : `No corruption signals detected.`,
      procurementRiskScore > 60
        ? `⚠️ High procurement risk (${procurementRiskScore.toFixed(1)}%). Reduce corruption signals and conflict flags.`
        : `Procurement risk within acceptable parameters.`,
    ];
  }

  if (panel === "TribunalPanel" || panel === "Procurement Tribunal") {
    const tribunalDecision = (inputs.tribunalDecision as string) || "pending";
    const remediationDemandEUR = (inputs.remediationDemandEUR as number) || 0;
    const appealWindowDays = (inputs.appealWindowDays as number) || 0;
    const weightedSeverity = (inputs.weightedSeverity as number) || 0;

    return [
      `Tribunal escalation impacts subsidy eligibility under EU GPP.`,
      `Current decision: ${tribunalDecision.toUpperCase()}. Remediation demand: €${remediationDemandEUR.toLocaleString()}.`,
      tribunalDecision === "disqualification"
        ? `⚠️ Disqualification decision. Vendor excluded from future procurements.`
        : tribunalDecision === "fine"
        ? `⚠️ Fine imposed. Vendor must pay remediation demand within appeal window.`
        : tribunalDecision === "warning"
        ? `Warning issued. Monitor vendor compliance closely.`
        : `No decision rendered yet. Generate decision to proceed.`,
      appealWindowDays > 0
        ? `Appeal window: ${appealWindowDays} days remaining. Appeal to reduce remediation demand by 15%.`
        : `No appeal window active.`,
      weightedSeverity >= 70
        ? `High weighted severity (${weightedSeverity.toFixed(1)}) indicates serious violations.`
        : weightedSeverity >= 40
        ? `Moderate weighted severity (${weightedSeverity.toFixed(1)}) requires attention.`
        : `Low weighted severity (${weightedSeverity.toFixed(1)}). Monitor for compliance.`,
    ];
  }

  if (panel === "PassportRegistryPanel" || panel === "ESG Passport Registry") {
    const registryHealth = (inputs.registryHealth as number) || 100;
    const revokedCount = (inputs.revokedCount as number) || 0;
    const passportCount = (inputs.passportCount as number) || 0;
    const distribution = (inputs.passportLevelDistribution as { basic: number; enhanced: number; sovereign: number }) || { basic: 0, enhanced: 0, sovereign: 0 };

    return [
      `Sovereign passports unlock premium tender preferences.`,
      `Registry health: ${registryHealth.toFixed(1)}%. Total passports: ${passportCount}.`,
      registryHealth >= 80
        ? `Excellent registry health. Maintain compliance to preserve status.`
        : registryHealth >= 50
        ? `Moderate registry health. Reduce revoked count and update latency.`
        : `⚠️ Low registry health (${registryHealth.toFixed(1)}%). Immediate action required.`,
      revokedCount > 0
        ? `⚠️ Revoked passports: ${revokedCount}. Review revocation reasons and improve entity compliance.`
        : `No revoked passports. Registry integrity maintained.`,
      `Passport distribution: ${distribution.basic} basic, ${distribution.enhanced} enhanced, ${distribution.sovereign} sovereign.`,
      distribution.sovereign > 0
        ? `Sovereign passports (${distribution.sovereign}) provide highest tender preference.`
        : `No sovereign passports issued. Encourage entities to improve ESG scores above 76%.`,
    ];
  }

  if (panel === "BeneficialGraphPanel" || panel === "Beneficial Ownership Graph") {
    const conflictScore = (inputs.conflictScore as number) || 0;
    const oligarchClusterScore = (inputs.oligarchClusterScore as number) || 0;
    const integrityRisk = (inputs.integrityRisk as number) || 0;
    const nepotismFlags = (inputs.nepotismFlags as number) || 0;
    const tenderCollisions = (inputs.tenderCollisionCount as number) || 0;
    const graphDepth = (inputs.graphDepth as number) || 0;

    return [
      `Oligarchic clustering increases governance downgrade and subsidy reduction.`,
      integrityRisk >= 70
        ? `Critical integrity risk: ${integrityRisk.toFixed(1)}%. Immediate governance audit required.`
        : integrityRisk >= 50
        ? `Moderate integrity risk: ${integrityRisk.toFixed(1)}%. Review ownership structures.`
        : `Integrity risk: ${integrityRisk.toFixed(1)}% within acceptable range.`,
      conflictScore >= 50
        ? `Conflict score ${conflictScore.toFixed(1)}% indicates significant relationship conflicts.`
        : `Conflict score ${conflictScore.toFixed(1)}% acceptable.`,
      oligarchClusterScore >= 70
        ? `Oligarch cluster score ${oligarchClusterScore.toFixed(1)}% shows high concentration of power. Disclosure required.`
        : `Oligarch cluster score ${oligarchClusterScore.toFixed(1)}% manageable.`,
      nepotismFlags > 0
        ? `${nepotismFlags} nepotism flags detected. Investigate family connections.`
        : `No nepotism flags detected.`,
      tenderCollisions > 0
        ? `${tenderCollisions} tender collision(s) identified. Conflict of interest review needed.`
        : `No tender collisions detected.`,
      graphDepth > 5
        ? `Graph depth ${graphDepth} indicates complex ownership structures. Enhanced disclosure required.`
        : `Graph depth ${graphDepth} within manageable complexity.`,
    ];
  }

  if (panel === "SubsidyEligibilityPanel" || panel === "Subsidy Eligibility Engine") {
    const subsidyScore = (inputs.subsidyEligibilityScore as number) || 100;
    const grantReduction = (inputs.grantReductionPercent as number) || 0;
    const riskOfMisuse = (inputs.riskOfMisuseFlag as boolean) || false;
    const escalationPath = (inputs.escalationPath as string) || "none";
    const corruptionSignals = (inputs.corruptionSignals as number) || 0;
    const passportLevel = (inputs.passportLevel as string) || "basic";

    return [
      `Eligibility degraded due to procurement volatility and corruption signals. Consider remediation under Article 9.`,
      `Subsidy eligibility score: ${subsidyScore.toFixed(1)}%. Grant reduction: ${grantReduction.toFixed(1)}%.`,
      subsidyScore >= 70
        ? `High eligibility score. Subsidy access granted without restrictions.`
        : subsidyScore >= 50
        ? `Moderate eligibility score. Regional audit may be required.`
        : `⚠️ Low eligibility score (${subsidyScore.toFixed(1)}%). EU audit or prosecution may be triggered.`,
      riskOfMisuse
        ? `⚠️ Risk of misuse flag is active. Immediate remediation required.`
        : `No misuse risk detected.`,
      escalationPath === "prosecution"
        ? `⚠️ Prosecution escalation path activated. Legal review required.`
        : escalationPath === "euAudit"
        ? `⚠️ EU audit escalation path activated. Prepare comprehensive documentation.`
        : escalationPath === "regionalAudit"
        ? `Regional audit escalation path activated. Monitor compliance closely.`
        : `No escalation required. Status: compliant.`,
      corruptionSignals > 0
        ? `⚠️ Corruption signals detected (${corruptionSignals}). This significantly impacts eligibility.`
        : `No corruption signals. Integrity maintained.`,
      passportLevel === "sovereign"
        ? `Sovereign passport provides +10 bonus to eligibility score.`
        : passportLevel === "enhanced"
        ? `Enhanced passport provides +5 bonus to eligibility score.`
        : `Basic passport provides no bonus. Consider upgrading to enhanced/sovereign level.`,
    ];
  }

  if (panel === "JudicialTriggerPanel" || panel === "Judicial Trigger Engine") {
    const conflictScore = (inputs.conflictScore as number) || 0;
    const oligarchClusterScore = (inputs.oligarchClusterScore as number) || 0;
    const procurementRiskScore = (inputs.procurementRiskScore as number) || 0;
    const integrityRisk = (inputs.integrityRisk as number) || 0;
    const triggerSeverity = (inputs.triggerSeverity as number) || 0;
    const judicialAction = (inputs.judicialAction as string) || "MonitorOnly";
    const evidenceAdmissibility = (inputs.evidenceAdmissibility as string) || "medium";
    const tribunalDecision = (inputs.tribunalDecision as string) || "pending";
    const geopoliticalWeight = (inputs.geopoliticalWeight as number) || 0;

    return [
      `Trigger escalation determined under Anti-Corruption Directive 2014/42/EU.`,
      `Judicial action: ${judicialAction}. Trigger severity: ${triggerSeverity.toFixed(1)}%.`,
      triggerSeverity >= 75
        ? `⚠️ EU-Prosecution level triggered. High-risk case requires immediate attention.`
        : triggerSeverity >= 60
        ? `⚠️ National investigation recommended. Evidence review required.`
        : triggerSeverity >= 40
        ? `Regional investigation threshold reached. Monitor closely.`
        : `Current risk level within monitoring range.`,
      evidenceAdmissibility === "low"
        ? `⚠️ Low evidence admissibility (${evidenceAdmissibility}) may affect case viability. Strengthen evidence chain.`
        : evidenceAdmissibility === "medium"
        ? `Evidence admissibility: ${evidenceAdmissibility}. Maintain documentation standards.`
        : `High evidence admissibility (${evidenceAdmissibility}) supports strong case foundation.`,
      tribunalDecision === "disqualification"
        ? `⚠️ Tribunal disqualification increases trigger severity by +10 points.`
        : tribunalDecision === "fine"
        ? `Tribunal fine issued. Consider impact on judicial escalation.`
        : tribunalDecision === "warning"
        ? `Tribunal warning active. Monitor for escalation.`
        : `No active tribunal decision.`,
      geopoliticalWeight > 3
        ? `⚠️ High geopolitical weight (${geopoliticalWeight.toFixed(1)}/5.0) increases cross-border complexity.`
        : geopoliticalWeight > 0
        ? `Geopolitical weight: ${geopoliticalWeight.toFixed(1)}/5.0. Consider international implications.`
        : `Low geopolitical weight. Focus on domestic compliance.`,
    ];
  }

  if (panel === "SatelliteOverlayPanel" || panel === "Satellite Overlay Risk") {
    const overlayRiskIndex = (inputs.overlayRiskIndex as number) || 0;
    const progressMismatch = (inputs.progressMismatch as number) || 0;
    const ghostSiteFlag = (inputs.ghostSiteFlag as boolean) || false;
    const satelliteDriftScore = (inputs.satelliteDriftScore as number) || 0;
    const landUseMismatch = (inputs.landUseMismatch as number) || 0;
    const procurementAnomaly = (inputs.procurementAnomaly as number) || 0;
    const crossBorderSuspicion = (inputs.crossBorderSuspicion as number) || 0;

    return [
      `Satellite drift and budget-progress mismatch indicate potential procurement fraud under EU Cohesion Fund rules.`,
      `Overlay risk index: ${overlayRiskIndex.toFixed(1)}%. Progress mismatch: ${progressMismatch.toFixed(1)}%.`,
      ghostSiteFlag
        ? `⚠️ GHOST CONSTRUCTION ALERT: Construction progress <10% but budget disbursement >30%. Suspicious pattern indicates potential fraud.`
        : overlayRiskIndex >= 75
        ? `⚠️ Critical overlay risk (${overlayRiskIndex.toFixed(1)}%). High probability of procurement fraud. Immediate audit required.`
        : overlayRiskIndex >= 50
        ? `⚠️ High overlay risk (${overlayRiskIndex.toFixed(1)}%). Investigate budget-progress alignment and site verification.`
        : overlayRiskIndex >= 25
        ? `Moderate overlay risk (${overlayRiskIndex.toFixed(1)}%). Monitor site progress and budget disbursement patterns.`
        : `Low overlay risk (${overlayRiskIndex.toFixed(1)}%). Current indicators within acceptable range.`,
      satelliteDriftScore >= 70
        ? `⚠️ High satellite drift score (${satelliteDriftScore.toFixed(1)}). Verify site coordinates match reported location.`
        : satelliteDriftScore >= 40
        ? `Moderate satellite drift (${satelliteDriftScore.toFixed(1)}). Review site location accuracy.`
        : `Satellite drift within acceptable range (${satelliteDriftScore.toFixed(1)}).`,
      procurementAnomaly >= 70
        ? `⚠️ High procurement anomaly (${procurementAnomaly.toFixed(1)}%). Review procurement process for irregularities.`
        : procurementAnomaly >= 40
        ? `Moderate procurement anomaly (${procurementAnomaly.toFixed(1)}%). Verify procurement compliance.`
        : `Procurement anomaly within acceptable range (${procurementAnomaly.toFixed(1)}%).`,
      landUseMismatch >= 70
        ? `⚠️ High land use mismatch (${landUseMismatch.toFixed(1)}%). Verify approved land use versus actual construction.`
        : landUseMismatch >= 40
        ? `Moderate land use mismatch (${landUseMismatch.toFixed(1)}%). Review land use compliance.`
        : `Land use mismatch within acceptable range (${landUseMismatch.toFixed(1)}%).`,
      crossBorderSuspicion >= 4
        ? `⚠️ High cross-border suspicion (${crossBorderSuspicion.toFixed(1)}/5.0). Investigate international transaction patterns.`
        : crossBorderSuspicion >= 2
        ? `Moderate cross-border suspicion (${crossBorderSuspicion.toFixed(1)}/5.0). Monitor international activities.`
        : `Low cross-border suspicion (${crossBorderSuspicion.toFixed(1)}/5.0).`,
    ];
  }

  if (panel === "SettlementPanel" || panel === "Settlement Ledger" || panel?.toLowerCase().includes("settlement")) {
    // Settlement ledger insights
    const entries = (inputs.entries as any[]) || [];
    const balances = (inputs.balances as Record<string, number>) || {};
    
    const entities = Object.keys(balances);
    const negativeEntities = entities.filter(e => (balances[e] || 0) < -5000);
    const positiveEntities = entities.filter(e => (balances[e] || 0) > 10000);
    
    const penaltyEntries = entries.filter(e => e.reason?.startsWith("PENALTY:"));
    const overrideEntries = entries.filter(e => e.sovereignOverride);
    
    const insights = [
      `Settlement ledger contains ${entries.length} entries affecting ${entities.length} entities.`,
      entities.length > 0
        ? `Balance range: ${Math.min(...entities.map(e => balances[e] || 0)).toFixed(2)} to ${Math.max(...entities.map(e => balances[e] || 0)).toFixed(2)}.`
        : `No active balances.`,
    ];
    
    if (negativeEntities.length > 0) {
      insights.push(`⚠️ ${negativeEntities.length} entity/entities with large negative balances (<-5000). Consider rebalance flows.`);
      negativeEntities.slice(0, 3).forEach(e => {
        insights.push(`  • ${e}: ${balances[e].toFixed(2)}`);
      });
    }
    
    if (positiveEntities.length > 0) {
      insights.push(`✓ ${positiveEntities.length} entity/entities with high positive balances (>10000). Potential over-allocation detected.`);
    }
    
    if (penaltyEntries.length > 0) {
      const penaltyStreaks = new Map<string, number>();
      penaltyEntries.forEach(e => {
        const entity = e.from || e.to || "unknown";
        penaltyStreaks.set(entity, (penaltyStreaks.get(entity) || 0) + 1);
      });
      
      const maxStreak = Math.max(...Array.from(penaltyStreaks.values()));
      if (maxStreak > 3) {
        insights.push(`⚠️ Penalty streak detected: ${maxStreak} consecutive penalties for ${Array.from(penaltyStreaks.entries()).find(([_, count]) => count === maxStreak)?.[0] || "entity"}.`);
      }
    }
    
    if (overrideEntries.length > 10) {
      insights.push(`⚠️ Sovereign override misuse: ${overrideEntries.length} overrides in recent period. Threshold (10/day) exceeded.`);
    }
    
    const scopeAgg = entries.reduce((acc, e) => {
      acc[e.scope] = (acc[e.scope] || 0) + Math.abs(e.amount);
      return acc;
    }, {} as Record<string, number>);
    
    const topScope = Object.entries(scopeAgg).sort((a, b) => b[1] - a[1])[0];
    if (topScope) {
      insights.push(`Top settlement scope: ${topScope[0]} (${topScope[1].toFixed(2)} units).`);
    }
    
    return insights;
  }

  if (panel === "SubsidyAllocationPanel" || panel === "Subsidy Allocation" || panel?.toLowerCase().includes("subsidy allocation")) {
    const allocations = (inputs.subsidyAllocations as Record<string, any>) || {};
    const totalAllocated = (inputs.totalAllocated as number) || 0;
    const overExposed = (inputs.overExposedCountries as string[]) || [];
    const highFraudRisk = (inputs.highFraudRiskCountries as string[]) || [];
    
    const insights = [
      `EU Cross-Sovereign Subsidy Allocation: ${Object.keys(allocations).length} countries tracked.`,
      `Total allocated: €${(totalAllocated / 1000000).toFixed(1)}M.`,
    ];
    
    if (overExposed.length > 0) {
      insights.push(`⚠️ ${overExposed.length} country/countries over-exposed (>90% of plafon): ${overExposed.join(", ")}. Immediate review required.`);
    }
    
    if (highFraudRisk.length > 0) {
      insights.push(`🔴 ${highFraudRisk.length} country/countries with high fraud risk: ${highFraudRisk.join(", ")}. Enhanced monitoring recommended.`);
    }
    
    const countries = Object.values(allocations);
    const nonCompliant = countries.filter((c: any) => !c.esgCompliance);
    if (nonCompliant.length > 0) {
      insights.push(`⚠️ ${nonCompliant.length} country/countries with ESG compliance issues. Subsidy allocation may be restricted.`);
    }
    
    const electionSensitive = countries.filter((c: any) => (c.electionYearSensitivity || 0) >= 3);
    if (electionSensitive.length > 0) {
      insights.push(`⚠️ ${electionSensitive.length} country/countries with high election-year sensitivity (≥3/5). Increased scrutiny recommended.`);
    }
    
    const avgUtilization = countries.reduce((sum: number, c: any) => {
      const util = c.plafon > 0 ? (c.allocated / c.plafon) * 100 : 0;
      return sum + util;
    }, 0) / countries.length;
    
    insights.push(`Average country utilization: ${avgUtilization.toFixed(1)}%.`);
    
    return insights;
  }

  if (panel === "SovereignReconciliationPanel" || panel === "Sovereign Reconciliation" || panel?.toLowerCase().includes("sovereign reconciliation")) {
    const summary = (inputs.reconciliationSummary as any) || {};
    const totalReconciliations = summary.totalReconciliations || 0;
    const totalAmount = summary.totalAmount || 0;
    const byCountry = summary.byCountry || {};
    
    const insights = [
      `Sovereign Reconciliation: ${totalReconciliations} reconciliation(s) processed.`,
      `Total amount reconciled: ${totalAmount.toLocaleString()} CO₂/ESG credits.`,
    ];
    
    const countries = Object.keys(byCountry);
    if (countries.length > 0) {
      const netPositives = countries.filter((c) => (byCountry[c]?.net || 0) > 0);
      const netNegatives = countries.filter((c) => (byCountry[c]?.net || 0) < 0);
      
      if (netPositives.length > 0) {
        insights.push(`✓ ${netPositives.length} country/countries with net positive balance (received > sent).`);
        netPositives.slice(0, 3).forEach((c) => {
          insights.push(`  • ${c}: +${byCountry[c].net.toLocaleString()}`);
        });
      }
      
      if (netNegatives.length > 0) {
        insights.push(`⚠️ ${netNegatives.length} country/countries with net negative balance (sent > received).`);
        netNegatives.slice(0, 3).forEach((c) => {
          insights.push(`  • ${c}: ${byCountry[c].net.toLocaleString()}`);
        });
      }
      
      const topSender = countries.reduce((max, c) => 
        (byCountry[c]?.sent || 0) > (byCountry[max]?.sent || 0) ? c : max, countries[0]
      );
      const topReceiver = countries.reduce((max, c) => 
        (byCountry[c]?.received || 0) > (byCountry[max]?.received || 0) ? c : max, countries[0]
      );
      
      if (topSender && byCountry[topSender]?.sent > 0) {
        insights.push(`Top sender: ${topSender} (${byCountry[topSender].sent.toLocaleString()} credits sent).`);
      }
      
      if (topReceiver && byCountry[topReceiver]?.received > 0) {
        insights.push(`Top receiver: ${topReceiver} (${byCountry[topReceiver].received.toLocaleString()} credits received).`);
      }
    }
    
    const auditEntries = (inputs.auditEntries as number) || 0;
    insights.push(`Compliance audit trail: ${auditEntries} entries. All reconciliations tracked for EU compliance.`);
    
    return insights;
  }

  return [`No insights yet. Provide more input.`];
}





